"use client";

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import SocketContext from "@/socket/Context";
import VideoPlayerSide from "./player-side";
import styles from "./video.module.scss";
import services from "@/store/services";
import AllTours from "@/data/AllTours";
import Components from "@/components";
import utils from "@/utils";

function VideoDashboard() {
  const enrollments = useSelector((state) => state?.enrollment);
  const { testLinkModal } = useSelector((state) => state.modal);
  const { newVideoPage } = useSelector((state) => state.tours);
  const { courseInfo } = useSelector((state) => state.video);
  const { socket } = useContext(SocketContext).SocketState;
  const user = useSelector((state) => state?.user?.users);
  const { testInfo } = useSelector((state) => state.video);
  const [isSrcChanged, setIsSrcChanged] = useState(false);
  const dispatch = useDispatch();

  const [showTestModal, setShowTestModal] = useState(false);
  const [isTestWarning, setIsTestWarning] = useState({
    show: false,
    type: "smallScreenTest",
    btnClick: "Got It",
    width: false,
  });

  const stageScores = useMemo(
    () =>
      enrollments?.length
        ? enrollments.find((item) => item?.courseId === courseInfo?._id)?.scores
        : [],
    [enrollments, courseInfo]
  );

  const stageStats = useMemo(
    () => stageScores?.find((score) => score.stageId === testInfo?.stageId),
    [stageScores, testInfo]
  );

  const attemptsCount = stageStats?.attempts;
  const currBestMarks = stageStats?.marks;
  const totalMarks = stageStats?.total;

  const avgMarks = useMemo(
    () =>
      stageScores?.length > 0
        ? stageScores?.reduce((sum, s) => sum + s.marks, 0) /
          stageScores?.length
        : 0,
    [stageScores]
  );

  const toggleTestLinkModal = () =>
    dispatch(services.toggleTestLinkModal(false));

  const toggleVideoPageModal = () => {
    dispatch(services.disableTour(user?._id, "videoPageTour", socket));
  };

  const communicationsUpdated = useCallback(() => {
    socket?.on("tour:disabled", (res) => {
      if (res.userId === user?._id) {
        dispatch(services.updateCommunications(res));
      }
    });

    return () => {
      socket?.off("tour:disabled");
    };
  }, [socket]);

  useEffect(() => {
    communicationsUpdated();
  }, [communicationsUpdated]);

  const shouldShowWarning = useCallback(() => {
    if (!isTestWarning.show) {
      setShowTestModal(testLinkModal);
    } else {
      setShowTestModal(false);
    }
  }, [testLinkModal]);

  const shouldShowModal = useCallback(() => {
    if (user?.communications) {
      const videoTour = user?.communications[AllTours.videoPageTour];
      if (videoTour && videoTour === true)
        dispatch(services.startNewVideoPageTour());
    }
  }, [user?.communications]);

  useEffect(() => {
    shouldShowWarning();
  }, [shouldShowWarning]);

  useEffect(() => {
    shouldShowModal();
  }, [shouldShowModal]);

  return (
    <section className={styles["container"]}>
      <Components.Modal
        placement="center"
        showModal={showTestModal}
        setShowModal={toggleTestLinkModal}
        className={styles["modal-test-link"]}
      >
        {attemptsCount >= 3 ? (
          <Components.NoAttemptsModal
            currBestMarks={currBestMarks}
            avgMarks={avgMarks}
            totalMarks={totalMarks}
            closeHandler={() => toggleTestLinkModal(false)}
          />
        ) : (
          <Components.TestLinkModals
            content={[
              {
                title: testInfo?.title,
                description: testInfo.description,
              },
            ]}
            marksStats={[
              {
                currBestMarks,
                avgMarks,
                totalMarks,
              },
            ]}
            attemptsCount={attemptsCount}
            closeHandler={() => toggleTestLinkModal(false)}
          />
        )}
      </Components.Modal>

      {courseInfo && (
        <Components.Modal
          block={true}
          placement="center"
          showModal={newVideoPage}
          className={styles["modal-video-tour"]}
          setShowModal={toggleVideoPageModal}
        >
          <Components.VideoPageModal
            closeHandler={() => toggleVideoPageModal()}
            skipHandler={() => toggleVideoPageModal()}
          />
        </Components.Modal>
      )}

      {isTestWarning.show && testInfo.title ? (
        <Components.WarningAlert
          type={isTestWarning.type}
          onCancel={() => {
            toggleTestLinkModal(false);
            setIsTestWarning((prev) => ({ ...prev, show: false }));
          }}
        />
      ) : null}

      <VideoPlayerSide
        isSrcChanged={isSrcChanged}
        changeIsSrcChange={() => setIsSrcChanged(false)}
        tour={utils.getFromLocalStorage("video-tour-guide")?.show === "YES"}
      />
    </section>
  );
}

VideoDashboard.proTypes = {
  panelContent: PropTypes.object,
  panelActive: PropTypes.string,
  onVideoEnd: PropTypes.func,
  certificateReq: PropTypes.func,
};

export default VideoDashboard;
